// THE PARTY PAGE'S STATEMENT TAB — laid out as Money movements is (his words, 1 Nov 2026: "the party statement is not
// aligned with other statement"): the search first, ONE Filters button (Asset, Show reversed), the period as one button
// (All time by default — a statement is the whole story); the TIE in one quiet line (tie.ts: the statement's closing per
// asset against the route's box and the balance the page shows — "ties", or the two figures that disagree; a window that
// stops before today names its closing day and claims no tie); one register
// (StatementList), newest first; Astra's footer — "N records · Newest first" … "Dubai time".
//
// THE READ (statementRead.ts) walks every page of the window before anything is drawn newest first; a window too long to
// read whole, or a later page that failed, is said above the list. The search runs over the rows read (the route has no
// search): the reference, the deal and the ledger's sentence.
import { useEffect, useMemo, useState } from 'react';
import { Chip } from '../../FilterRail';
import { FilterSelect } from '../../FilterBar';
import { periodDays, type PeriodValue } from '../../PeriodChips';
import { PeriodButton, PeriodPanel } from '../movements/PeriodControl';
import { EmptyState, Panel, Pending, ReadFailed, SearchBar, SkeletonRows, StateLine } from '../kit';
import { displayAsset, dubaiDate, fmtAmount, fmtDay } from '../../../lib/format';
import { compareRelevance } from '../../../lib/naming';
import { boxParts } from '../../BoxLines';
import { STATEMENT_MAX_PAGES, STATEMENT_PAGE, matchesSearch, statementAssets, usePartyJournal } from './statementRead';
import { StatementList } from './StatementList';
import type { JournalRow } from './journal';
import { closesBeforeToday, reconcileDp, statementTies, type Tie } from './tie';

const SHOWN_STEP = 50;

export function PartyStatementTab({ slug, entity, balances, assets, onOpen, self, dealLifecycle }: {
  /** The party as the ledger's sentences name them. */
  self?: string;
  dealLifecycle?: (id: string) => string | null | undefined;
  slug: string;
  entity: string;
  balances: Record<string, number>;
  /** The party read's assets (its box first); the statement's own list — settled ones too — is merged in for the filter. */
  assets: string[];
  onOpen: (r: JournalRow) => void;
}) {
  const [typed, setTyped] = useState('');
  const [asset, setAsset] = useState('');
  const [undone, setUndone] = useState(false);
  const [period, setPeriod] = useState<PeriodValue>({ key: 'all' });
  const [periodOpen, setPeriodOpen] = useState(false);
  const win = periodDays(period);
  const read = usePartyJournal(slug, entity, balances, { asset, from: win.from, to: win.to, undone });
  const matched = useMemo(() => read.journal.filter((r) => matchesSearch(r, typed)), [read.journal, typed]);
  // FIFTY AT A TIME (the audit, 1 Nov 2026: a hundred put Show more 7,000 px down): the newest fifty, then Show more (the
  // read itself is whole).
  const [shown, setShown] = useState(SHOWN_STEP);
  const rows = matched.slice(0, shown);
  const more = matched.length - rows.length;
  useEffect(() => { setShown(SHOWN_STEP); }, [typed, asset, undone, period]);

  const behind = [!!asset, undone].filter(Boolean).length;
  const filtered = behind > 0 || !!typed.trim() || period.key !== 'all';
  const clearAll = () => { setTyped(''); setAsset(''); setUndone(false); setPeriod({ key: 'all' }); };
  // EVERY ASSET THAT EVER TOUCHED THEM: a currency settled back to square has no balance but has history to filter on.
  const assetOptions = useMemo(() => statementAssets(assets, read.ever, read.box).map((a) => ({ value: a, label: displayAsset(a) })),
    [assets, read.ever, read.box]);

  // THE TIE, once every page of the window is read (never over a partial read).
  const closesOn = closesBeforeToday(win.to, dubaiDate()) ? fmtDay(`${win.to}T12:00:00+04:00`) : null;
  const ties = read.complete ? statementTies(read.rows, read.box, balances)
    .sort((x, y) => compareRelevance({ asset: x.asset, balance: x.end }, { asset: y.asset, balance: y.end })) : [];
  // THE CLOSING AS LABELLED CELLS (his "one fact per place", 1 Nov 2026: it was one line of unit, word, figure and "ties").
  const tie = ties.length > 0 && (
    <div aria-label="Tie" className="mb-4 flex flex-wrap gap-x-8 gap-y-3 rounded-[10px] border border-stroke bg-panel px-4 py-3 lg:px-5">
      {ties.map((t) => <TieWord key={t.asset} t={t} past={!!closesOn} closesOn={closesOn} />)}
    </div>
  );

  const footer = (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
      <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span>{matched.length} {matched.length === 1 ? 'record' : 'records'} · Newest first{more > 0 && ` · ${rows.length} shown`}</span>
        {more > 0 && <button type="button" className="tap-44 btn-link text-meta" onClick={() => setShown((n) => n + SHOWN_STEP)}>Show more</button>}
      </span>
      <span>Dubai time</span>
    </div>
  );

  return (
    <>
      {tie}
      <SearchBar
        value={typed}
        onChange={setTyped}
        placeholder="Search reference or note"
        active={behind}
        extra={<PeriodButton value={period} open={periodOpen} onToggle={() => setPeriodOpen((v) => !v)} />}
        filters={(
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
              <div className="min-w-0">
                <div className="mb-1 text-label font-medium uppercase tracking-wider text-tg-hint">Asset</div>
                <FilterSelect value={asset} onChange={setAsset} allValue="" className="lg:w-full" options={assetOptions} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Chip active={undone} onClick={() => setUndone((v) => !v)}>Show reversed</Chip>
              {filtered && <button type="button" onClick={clearAll} className="btn-link text-body">Clear filters</button>}
            </div>
          </div>
        )}
      />
      {periodOpen && <PeriodPanel value={period} onChange={setPeriod} onDone={() => setPeriodOpen(false)} />}

      {read.state === 'failed' && !read.pageFailed ? (
        <Panel><ReadFailed what="the statement" error={read.error} onRetry={read.refetch} tone="bad" /></Panel>
      ) : !read.complete && !read.capped && !read.pageFailed ? (
        // EVERY PAGE OF THE WINDOW FIRST: the newest entry is on the last one.
        <Panel flush>
          <div className="px-4 pt-4 text-tg-hint lg:px-5"><Pending what="Reading the statement…" /></div>
          <SkeletonRows rows={4} what="the statement" />
        </Panel>
      ) : (
        <>
          {read.capped && (
            <StateLine tone="warn" className="mb-4" more={`The route pages oldest first; ${(STATEMENT_MAX_PAGES * STATEMENT_PAGE).toLocaleString('en-US')} entries were read.`}>
              Too long to read whole — pick a shorter period.
            </StateLine>
          )}
          {read.pageFailed && (
            <div className="mb-4"><ReadFailed what="the rest of the statement" error={read.error} onRetry={read.fetchRest} tone="warn" /></div>
          )}
          {rows.length > 0 ? (
            <Panel flush>
              <StatementList rows={rows} onOpen={onOpen} self={self} dealLifecycle={dealLifecycle} />
              {footer}
            </Panel>
          ) : (
            <Panel>
              <EmptyState
                className="lg:py-12"
                action={filtered ? <button type="button" onClick={clearAll} className="btn-link">Clear filters</button> : undefined}
              >
                {period.key === 'all' && !typed.trim() && !asset ? 'No entries yet.' : 'Nothing in this period.'}
              </EmptyState>
            </Panel>
          )}
        </>
      )}
    </>
  );
}

/** One asset's closing as a cell: "Closing INR · you hold" over the figure, and "Ties" beside it when the statement, the
 *  route's box and the page's balance agree; else the two figures that disagree, each labelled, in amber, at the precision
 *  they differ at. A past closing day names its day and claims nothing. */
export function TieWord({ t, past, closesOn = null }: { t: Tie; past: boolean; closesOn?: string | null }) {
  const { words, figure } = boxParts(t.end, t.asset);
  const label = `${closesOn ? `Closing ${closesOn}` : 'Closing'} · ${displayAsset(t.asset)}${figure ? ` · ${words}` : ''}`;
  const head = <div className="text-label font-medium uppercase tracking-wider text-tg-hint">{label}</div>;
  if (past || (t.ok && t.agrees)) {
    return (
      <div className="min-w-0">
        {head}
        <div className="mt-1 flex items-baseline gap-3 whitespace-nowrap">
          <span className="font-mono tabular text-body font-semibold">{figure || 'square'}</span>
          {!past && <span className="text-meta text-pos">Ties</span>}
        </div>
      </div>
    );
  }
  const against = t.ok ? t.shown : t.held;
  const dp = reconcileDp(t.end, against);
  return (
    <div className="min-w-0 text-warn">
      {head}
      <div className="mt-1 grid grid-cols-[auto_auto] gap-x-3 whitespace-nowrap text-body">
        <span>Here</span><span className="text-right font-mono tabular">{fmtAmount(t.end, t.asset, dp)}</span>
        <span>{t.ok ? 'Page shows' : 'Books hold'}</span><span className="text-right font-mono tabular">{fmtAmount(against, t.asset, dp)}</span>
      </div>
    </div>
  );
}
